import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { cn } from '@/shared/utils'
import {
  Mail, Phone, MessageCircle, Headphones, Send,
  CheckCircle2, ChevronDown,
  Loader2, User, AtSign, FileText, MessageSquare,
} from 'lucide-react'

// ── Fonts ───────────────────────────────────────────────────────────────────
// Bebas Neue = condensed highway-signage display face (the page's one loud voice)
// Inter = body workhorse · JetBrains Mono = dashboard/instrument digits
function useSupportFonts() {
  useEffect(() => {
    const id = 'drivys-support-fonts'
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;700&display=swap'
    document.head.appendChild(link)
  }, [])
}

// ── Data ────────────────────────────────────────────────────────────────────
// NOTE: WhatsApp / Live Chat CTAs are stubbed (href="#") — wire up to your
// real wa.me link and chat-widget trigger once available.
function getChannels(t: TFunction) {
  return [
    {
      icon: Mail, title: t('support.channels.email.title'), detail: 'info@drivys.com', meta: t('support.channels.email.meta'),
      accent: 'amber', cta: t('support.channels.email.cta'), href: 'mailto:info@drivys.com',
    },
    {
      icon: Phone, title: t('support.channels.phone.title'), detail: '8005008', meta: t('support.channels.phone.meta'),
      accent: 'slate', cta: t('support.channels.phone.cta'), href: 'tel:8005008',
    },
    {
      icon: MessageCircle, title: t('support.channels.whatsapp.title'), detail: t('support.channels.whatsapp.detail'), meta: t('support.channels.whatsapp.meta'),
      accent: 'green', cta: t('support.channels.whatsapp.cta'), href: '#',
    },
    {
      icon: Headphones, title: t('support.channels.liveChat.title'), detail: t('support.channels.liveChat.detail'), meta: t('support.channels.liveChat.meta'),
      accent: 'blue', cta: t('support.channels.liveChat.cta'), href: '#',
    },
  ]
}

function getFaqs(t: TFunction) {
  return [1, 2, 3, 4, 5, 6].map(i => ({ q: t(`support.faq.q${i}`), a: t(`support.faq.a${i}`) }))
}

const ACCENT_CFG: Record<string, { tile: string; icon: string; bar: string; cta: string }> = {
  amber: { tile: 'bg-brand-50',  icon: 'text-brand-600',  bar: 'bg-brand-400',  cta: 'text-brand-700 hover:text-brand-800' },
  slate: { tile: 'bg-slate-100', icon: 'text-slate-700',  bar: 'bg-slate-400',  cta: 'text-slate-700 hover:text-slate-900' },
  green: { tile: 'bg-green-50',  icon: 'text-green-600',  bar: 'bg-green-400',  cta: 'text-green-700 hover:text-green-800' },
  blue:  { tile: 'bg-brand-50',   icon: 'text-brand-600',   bar: 'bg-brand-400',   cta: 'text-brand-700 hover:text-brand-800' },
}

// ── Signature element: animated lane-marking divider ──────────────────────
function LaneDivider({ className }: { className?: string }) {
  return (
    <div
      className={cn('h-[6px] w-full rounded-full lane-divider motion-reduce:!animate-none', className)}
      aria-hidden="true"
    />
  )
}

// ── Section eyebrow ─────────────────────────────────────────────────────────
function Eyebrow({ children, dark }: { children: React.ReactNode; dark?: boolean }) {
  return (
    <div className={cn(
      "font-['JetBrains_Mono'] text-[13px] font-bold uppercase tracking-[0.2em] mb-2",
      dark ? 'text-brand-400' : 'text-brand-600'
    )}>
      {children}
    </div>
  )
}

// ── Contact channel card ─────────────────────────────────────────────────────
function ChannelCard({ channel }: { channel: ReturnType<typeof getChannels>[number] }) {
  const cfg = ACCENT_CFG[channel.accent]
  const Icon = channel.icon
  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
      <div className={cn('h-1.5 w-full', cfg.bar)} aria-hidden="true" />
      <div className="p-6">
        <div className={cn('w-11 h-11 rounded-xl flex items-center justify-center mb-4', cfg.tile)}>
          <Icon size={20} className={cfg.icon} />
        </div>
        <div className="text-sm font-bold text-gray-900">{channel.title}</div>
        <div className="text-base font-semibold text-gray-800 mt-1">{channel.detail}</div>
        <div className="text-xs text-gray-400 mt-0.5">{channel.meta}</div>
        <a
          href={channel.href}
          className={cn('mt-4 inline-flex items-center gap-1 text-xs font-bold', cfg.cta)}
        >
          {channel.cta} <span aria-hidden="true" className="rtl:rotate-180">→</span>
        </a>
      </div>
    </div>
  )
}

// ── FAQ accordion item ───────────────────────────────────────────────────────
function FaqItem({ index, q, a, open, onToggle }: {
  index: number; q: string; a: string; open: boolean; onToggle: () => void
}) {
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`faq-panel-${index}`}
        className="w-full flex items-center justify-between gap-4 py-5 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 rounded-lg"
      >
        <span className="text-sm font-bold text-gray-900">{q}</span>
        <ChevronDown
          size={18}
          className={cn('flex-shrink-0 text-brand-500 transition-transform duration-200', open && 'rotate-180')}
        />
      </button>
      <div
        id={`faq-panel-${index}`}
        className={cn('overflow-hidden transition-all duration-200 ease-in-out', open ? 'max-h-40 pb-5' : 'max-h-0')}
      >
        <p className="text-sm text-gray-500 leading-relaxed pe-8">{a}</p>
      </div>
    </div>
  )
}

// ── Form field shell ──────────────────────────────────────────────────────────
function Field({ label, required, icon, children }: {
  label: string; required?: boolean; icon: React.ReactNode; children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-gray-700 mb-1.5 block">
        {label}{required && <span className="text-brand-500"> *</span>}
      </span>
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50/60 px-3 py-2.5 focus-within:ring-2 focus-within:ring-brand-400 focus-within:border-brand-400 focus-within:bg-white transition-colors">
        <span className="text-gray-400 flex-shrink-0">{icon}</span>
        {children}
      </div>
    </label>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════
export function SupportPage() {
  useSupportFonts()
  const { t } = useTranslation()

  const CHANNELS = getChannels(t)
  const FAQS = getFaqs(t)

  const [openFaq, setOpenFaq] = useState<number | null>(0)
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' })
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent'>('idle')

  const update = useCallback((key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [key]: e.target.value })), [])

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    setStatus('submitting')
    // TODO: wire this up to your real contact endpoint, e.g.
    // restClient.post(endpoints.support.contact, form)
    setTimeout(() => {
      setStatus('sent')
      setForm({ name: '', email: '', phone: '', subject: '', message: '' })
    }, 900)
  }, [form])

  return (
    <div className="font-['Inter'] -m-6 sm:-m-8">
      <style>{`
        .lane-divider {
          background-image: repeating-linear-gradient(90deg, #F2A93B 0px, #F2A93B 28px, transparent 28px, transparent 52px);
          background-size: 52px 100%;
          animation: lane-scroll 1.4s linear infinite;
        }
        @keyframes lane-scroll { from { background-position-x: 0; } to { background-position-x: 52px; } }
      `}</style>

      {/* ════════ CONTACT CHANNELS ════════ */}
      <section className="px-6 sm:px-10 py-16 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Eyebrow>{t('support.channels.eyebrow')}</Eyebrow>
            <h2 className="text-2xl font-bold text-gray-900">{t('support.channels.heading')}</h2>
          </div>
          <LaneDivider className="mb-10 max-w-xs mx-auto" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {CHANNELS.map(c => <ChannelCard key={c.title} channel={c} />)}
          </div>
        </div>
      </section>

      {/* ════════ SEND MESSAGE ════════ */}
      <section className="px-6 sm:px-10 py-16 bg-[#FBF7EF]">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <Eyebrow>{t('support.form.eyebrow')}</Eyebrow>
            <h2 className="text-2xl font-bold text-gray-900">{t('support.form.heading')}</h2>
          </div>

          {status === 'sent' ? (
            <div className="rounded-2xl border border-green-200 bg-green-50 p-8 text-center">
              <CheckCircle2 size={32} className="text-green-500 mx-auto mb-3" />
              <div className="text-sm font-bold text-green-800">{t('support.form.sentTitle')}</div>
              <p className="text-xs text-green-700 mt-1">{t('support.form.sentBody')}</p>
              <button
                onClick={() => setStatus('idle')}
                className="mt-4 text-xs font-bold text-green-700 underline hover:text-green-900"
              >
                {t('support.form.sendAnother')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={t('support.form.nameLabel')} required icon={<User size={15} />}>
                  <input required value={form.name} onChange={update('name')}
                    placeholder={t('support.form.namePlaceholder')}
                    className="w-full bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none" />
                </Field>
                <Field label={t('support.form.emailLabel')} required icon={<AtSign size={15} />}>
                  <input required type="email" value={form.email} onChange={update('email')}
                    placeholder={t('support.form.emailPlaceholder')}
                    className="w-full bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none" />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={t('support.form.phoneLabel')} icon={<Phone size={15} />}>
                  <input value={form.phone} onChange={update('phone')}
                    placeholder={t('support.form.phonePlaceholder')}
                    className="w-full bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none" />
                </Field>
                <Field label={t('support.form.subjectLabel')} required icon={<FileText size={15} />}>
                  <input required value={form.subject} onChange={update('subject')}
                    placeholder={t('support.form.subjectPlaceholder')}
                    className="w-full bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none" />
                </Field>
              </div>
              <Field label={t('support.form.messageLabel')} required icon={<MessageSquare size={15} className="mt-0.5" />}>
                <textarea required rows={4} value={form.message} onChange={update('message')}
                  placeholder={t('support.form.messagePlaceholder')}
                  className="w-full bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none resize-none" />
              </Field>

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white text-sm font-bold py-3 transition-colors"
              >
                {status === 'submitting'
                  ? <><Loader2 size={16} className="animate-spin" /> {t('support.form.sending')}</>
                  : <><Send size={16} /> {t('support.form.submit')}</>}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ════════ FAQs ════════ */}
      <section className="px-6 sm:px-10 py-16 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <Eyebrow>{t('support.faq.eyebrow')}</Eyebrow>
            <h2 className="text-2xl font-bold text-gray-900">{t('support.faq.heading')}</h2>
          </div>
          <div className="rounded-2xl border border-gray-200 px-6">
            {FAQS.map((f, i) => (
              <FaqItem
                key={f.q}
                index={i}
                q={f.q}
                a={f.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(o => o === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
